<template>
  <calm-page extra-class="bank" :title="$t('menu.bank')">
    <template slot="actions">
      <calm-button variant="primary" @click="handleCreate()">
        <i class="fas fa-plus mr-1"></i> {{ $t("new") }}
      </calm-button>
    </template>

    <!-- ═══════════════ SEARCH BAR ═══════════════ -->
    <calm-filter-bar :hide-actions="true">
      <calm-field :label="$t('search')">
        <input
          v-model="searchQuery"
          type="text"
          class="ed"
          :placeholder="$t('search')"
        />
      </calm-field>
    </calm-filter-bar>

    <!-- ═══════════════ TABLE (skin the existing el-table, §11) ═══════════════ -->
    <calm-table
      :total="total"
      :from="from"
      :to="to"
      :per-page.sync="pagination.perPage"
      :current-page.sync="pagination.currentPage"
      :per-page-options="pagination.perPageOptions"
      :per-page-label="$t('per_page') !== 'per_page' ? $t('per_page') : 'ต่อหน้า'"
      :count-label="$t('data')"
      :to-label="$t('to')"
      :of-label="$t('of')"
      :empty-title="$t('no_data') !== 'no_data' ? $t('no_data') : 'ไม่พบข้อมูล'"
    >
      <el-table
        :data="queriedData"
        row-key="id"
        header-row-class-name="thead-light"
        @sort-change="sortChange"
        @selection-change="selectionChange"
      >
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          v-bind="column"
        >
        </el-table-column>

        <el-table-column min-width="180px" :label="$t('lmg.account_type')">
          <div slot-scope="{ row }" class="bank__pills">
            <span
              v-if="!row.AccountType"
              class="bank__pill bank__pill--unset"
            >{{ $t("lmg.account_type_unset") }}</span>
            <span
              v-else-if="row.AccountType === 1"
              class="bank__pill bank__pill--type"
            >{{ $t("lmg.account_type_hq") }}</span>
            <span
              v-else-if="row.AccountType === 2"
              class="bank__pill bank__pill--type"
            >{{ $t("lmg.account_type_branch_transfer") }}</span>
            <span
              v-else-if="row.AccountType === 3"
              class="bank__pill bank__pill--type"
            >{{ $t("lmg.account_type_branch_cash") }}</span>
            <span
              v-if="row.VisibleAllBranch === 1"
              class="bank__pill bank__pill--allbranch"
            >{{ $t("lmg.visible_all_branch") }}</span>
          </div>
        </el-table-column>

        <el-table-column min-width="160px" :label="$t('lmg.promptpay')">
          <div slot-scope="{ row }" class="bank__pills">
            <span
              v-if="row.PromptpayEnabled === 1"
              class="bank__pill bank__pill--pp"
            >PromptPay</span>
            <span
              v-if="row.ShowOnInvoice === 1"
              class="bank__pill bank__pill--inv"
            >{{ $t("lmg.on_invoice") }}</span>
            <span
              v-if="row.QrOnInvoice === 1"
              class="bank__pill bank__pill--qr"
            >QR</span>
          </div>
        </el-table-column>

        <el-table-column
          min-width="200px"
          align="right"
          :label="$t('actions')"
        >
          <div slot-scope="{ $index, row }" class="bank__rowact">
            <calm-button
              variant="ghost"
              size="sm"
              icon-only
              @click="handleEdit($index, row)"
            >
              <i class="fas fa-pen"></i>
            </calm-button>
            <calm-button
              variant="danger"
              size="sm"
              icon-only
              @click="handleDelete($index, row)"
            >
              <i class="fas fa-trash"></i>
            </calm-button>
          </div>
        </el-table-column>
      </el-table>
    </calm-table>

    <!-- ═══════════════ ADD / EDIT MODAL ═══════════════ -->
    <calm-modal
      :show.sync="showAddModal"
      :title="$t('lmg.bank')"
      :show-footer="false"
    >
      <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
        <form
          class="bank__form"
          @submit.prevent="handleSubmit(onUpdateBank)"
        >
          <base-input
            :label="$t('lmg.bank_name')"
            v-model="md.Name"
            :name="$t('lmg.bank_name')"
            rules="required"
          >
          </base-input>

          <base-input
            :label="$t('lmg.bank_account')"
            v-model="md.AccountNumber"
            :name="$t('lmg.bank_account')"
            rules="required"
          >
          </base-input>

          <!-- ── DMS cash-pool: account type + cross-branch visibility ── -->
          <div class="bank__field">
            <label class="bank__field__label">{{ $t("lmg.account_type") }}</label>
            <el-select
              v-model="md.accountType"
              class="bank__select"
              clearable
              :placeholder="$t('lmg.account_type')"
            >
              <el-option
                v-for="t in accountTypes"
                :key="t.ID"
                :label="t.Name"
                :value="t.ID"
              ></el-option>
            </el-select>
          </div>

          <div class="bank__opt">
            <el-checkbox v-model="md.visibleAllBranch">
              {{ $t("lmg.visible_all_branch") }}
            </el-checkbox>
          </div>

          <!-- ── PromptPay ── -->
          <div class="bank__opt">
            <el-checkbox v-model="md.promptpayEnabled">
              {{ $t("lmg.promptpay_enabled") }}
            </el-checkbox>
          </div>

          <template v-if="md.promptpayEnabled">
            <div class="bank__field">
              <label class="bank__field__label">{{ $t("lmg.promptpay_type") }}</label>
              <el-select v-model="md.promptpayType" class="bank__select">
                <el-option
                  v-for="t in promptpayTypes"
                  :key="t.ID"
                  :label="t.Name"
                  :value="t.ID"
                ></el-option>
              </el-select>
            </div>

            <base-input
              :label="$t('lmg.promptpay_id')"
              v-model="md.promptpayId"
              :name="$t('lmg.promptpay_id')"
            >
            </base-input>
          </template>

          <div class="bank__opt">
            <el-checkbox v-model="md.showOnInvoice">
              {{ $t("lmg.show_on_invoice") }}
            </el-checkbox>
          </div>

          <div class="bank__opt">
            <el-checkbox
              v-model="md.qrOnInvoice"
              :disabled="!md.promptpayEnabled"
            >
              {{ $t("lmg.qr_on_invoice") }}
            </el-checkbox>
          </div>

          <div class="calm__modal__actions">
            <calm-button
              variant="ghost"
              @click="showAddModal = false"
            >
              {{ $t("close") }}
            </calm-button>
            <calm-button variant="primary" native-type="submit">
              {{ $t("save") }}
            </calm-button>
          </div>
        </form>
      </validation-observer>
    </calm-modal>
  </calm-page>
</template>
<script>
import { Table, TableColumn, Select, Option, Checkbox } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "./PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    [Select.name]: Select,
    [Option.name]: Option,
    [Checkbox.name]: Checkbox,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  computed: {
    ...mapGetters({
      tableDataOrg: "Bank/getData"
    }),
    tableData: function() {
      // console.log(this.$route.query.id);
      // if (this.$route.query.id != null) {
      //   return this.tableDataOrg.filter((m) => {
      //     return m.EnterpriseID == "" + this.$route.query.id;
      //   });
      // }

      return this.tableDataOrg;
    },
    tableColumns: function() {
      return [
        {
          prop: "Name",
          label: this.$t("lmg.bank_name"),
          minWidth: 90,
          sortable: true
        },
        {
          prop: "AccountNumber",
          label: this.$t("lmg.bank_account"),
          minWidth: 100,
          sortable: true
        }
      ];
    }
  },
  data() {
    return {
      propsToSearch: ["Name", "AccountNumber"],
      selectedRows: [],
      showAddModal: false,
      // PromptPay receiver types — int values MUST match the backend
      // (reused verbatim from PromptPayTables.vue: 1=phone, 2=taxid/national-id, 3=e-wallet).
      promptpayTypes: [
        { ID: 1, Name: this.$t("lmg.pp_type_phone") },
        { ID: 2, Name: this.$t("lmg.pp_type_taxid") },
        { ID: 3, Name: this.$t("lmg.pp_type_ewallet") }
      ],
      // DMS cash-pool account types — int values MUST match the backend
      // (1=สนญ./head-office, 2=สาขาโอน/branch-transfer, 3=สาขาเงินสด/branch-cash).
      // 0 = unset (legacy accounts) — shown as placeholder, NOT defaulted to 1.
      accountTypes: [
        { ID: 1, Name: this.$t("lmg.account_type_hq") },
        { ID: 2, Name: this.$t("lmg.account_type_branch_transfer") },
        { ID: 3, Name: this.$t("lmg.account_type_branch_cash") }
      ],
      md: {
        ID: "",
        Name: "",
        AccountNumber: "",
        accountType: 0,
        visibleAllBranch: false,
        promptpayEnabled: false,
        promptpayType: 1,
        promptpayId: "",
        showOnInvoice: false,
        qrOnInvoice: false
      }
    };
  },
  mounted() {
    this.listData();
  },
  methods: {
    ...mapActions({
      createData: "Bank/createData",
      updateData: "Bank/updateData",
      deleteData: "Bank/deleteData",
      listData: "Bank/listData"
    }),
    handleCreate() {
      this.showAddModal = true;
      this.md.ID = 0;
      this.md.Name = "";
      this.md.AccountNumber = "";
      this.md.accountType = 0;
      this.md.visibleAllBranch = false;
      this.md.promptpayEnabled = false;
      this.md.promptpayType = 1;
      this.md.promptpayId = "";
      this.md.showOnInvoice = false;
      this.md.qrOnInvoice = false;
    },
    handleEdit(index, row) {
      this.showAddModal = true;
      this.md.ID = row.ID;
      this.md.Name = row.Name;
      this.md.AccountNumber = row.AccountNumber;
      this.md.accountType = row.AccountType || 0;
      this.md.visibleAllBranch = row.VisibleAllBranch === 1;
      this.md.promptpayEnabled = row.PromptpayEnabled === 1;
      this.md.promptpayType = row.PromptpayType || 1;
      this.md.promptpayId = row.PromptpayId || "";
      this.md.showOnInvoice = row.ShowOnInvoice === 1;
      this.md.qrOnInvoice = row.QrOnInvoice === 1;
    },
    onUpdateBank() {
      this.showAddModal = false;

      if (this.md.ID == 0) {
        this.createData({
          name: this.md.Name,
          accountNo: this.md.AccountNumber,
          accountType: this.md.accountType || 0,
          visibleAllBranch: this.md.visibleAllBranch ? 1 : 0,
          promptpayEnabled: this.md.promptpayEnabled ? 1 : 0,
          promptpayType: this.md.promptpayType,
          promptpayId: this.md.promptpayId,
          showOnInvoice: this.md.showOnInvoice ? 1 : 0,
          qrOnInvoice: this.md.qrOnInvoice ? 1 : 0
        })
          .then(resp => {
            this.listData();
            swal.fire({
              title: this.$t("create_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false
            });
          })
          .catch(err => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: this.getErrorText(err.response.data),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false
            });
          });
      } else {
        // update

        this.updateData({
          id: this.md.ID,
          name: this.md.Name,
          accountNo: this.md.AccountNumber,
          accountType: this.md.accountType || 0,
          visibleAllBranch: this.md.visibleAllBranch ? 1 : 0,
          promptpayEnabled: this.md.promptpayEnabled ? 1 : 0,
          promptpayType: this.md.promptpayType,
          promptpayId: this.md.promptpayId,
          showOnInvoice: this.md.showOnInvoice ? 1 : 0,
          qrOnInvoice: this.md.qrOnInvoice ? 1 : 0
        })
          .then(resp => {
            this.listData();
            swal.fire({
              title: this.$t("edit_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false
            });
          })
          .catch(err => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: this.getErrorText(err.response.data),
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false
            });
          });
      }
    },
    handleDelete(index, row) {
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question") + row.Name,
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            this.deleteData({
              id: row.ID
            })
              .then(resp => {
                this.listData();
                swal.fire({
                  title: this.$t("delete_success"),
                  text: ``,
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false
                });
              })
              .catch(err => {
                swal.fire({
                  title: this.$t(err.response.data.Msg),
                  text: this.getErrorText(err.response.data),
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false
                });
              });
          }
        });
    },
    selectionChange(selectedRows) {
      this.selectedRows = selectedRows;
    }
  }
};
</script>
<style scoped>
/* Bank CRUD — Calm skin. Namespaced under .bank so nothing leaks to other
   pages. The el-table is kept verbatim (§11 "skin, don't rewrite") and
   re-styled only via deep selectors below. */

/* Row-action cell: keep the two icon buttons inline + right-aligned. */
.bank__rowact {
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
}

/* Modal form: search field + stacked inputs + action row spacing. */
.bank__form >>> .form-group {
  margin-bottom: 16px;
}
.bank__form .calm__modal__actions {
  margin-top: 8px;
}

/* The free-text search box reuses the Kit's .ed input look. */
.bank >>> .ed {
  width: 100%;
}

/* Skin the existing <el-table> so it reads as a Calm surface without
   touching any column definitions or slots (§11). Deep selectors only. */
.bank >>> .el-table {
  color: var(--c-text, #2c3e50);
  background: transparent;
}
.bank >>> .el-table th,
.bank >>> .el-table .thead-light th {
  background: var(--c-surface-soft, #f4f7fb);
  color: var(--c-text-soft, #5b6b7e);
  font-weight: 600;
  border-bottom: 1px solid var(--c-border, #e3e9f0);
}
.bank >>> .el-table td {
  border-bottom: 1px solid var(--c-border, #eef2f7);
}
.bank >>> .el-table::before {
  background: var(--c-border, #e3e9f0);
}
.bank >>> .el-table__row:hover > td {
  background: var(--c-surface-soft, #f7fafd) !important;
}

/* ── PromptPay options in the add/edit modal ── */
.bank__opt {
  margin-bottom: 14px;
}
.bank__field {
  margin-bottom: 16px;
}
.bank__field__label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: var(--c-text-soft, #5b6b7e);
  font-size: 14px;
}
.bank__select {
  width: 100%;
}

/* ── Calm pills in the table ── */
.bank__pills {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bank__pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}
.bank__pill--pp {
  background: rgba(59, 110, 168, 0.12);
  color: var(--c-blue, #3b6ea8);
}
.bank__pill--inv {
  background: rgba(56, 134, 107, 0.12);
  color: var(--c-success, #38866b);
}
.bank__pill--qr {
  background: rgba(155, 89, 182, 0.14);
  color: #8e44ad;
}
.bank__pill--type {
  background: rgba(59, 110, 168, 0.12);
  color: var(--c-blue, #3b6ea8);
}
.bank__pill--unset {
  background: var(--c-surface-soft, #f4f7fb);
  color: var(--c-text-soft, #9aa6b4);
}
.bank__pill--allbranch {
  background: rgba(230, 162, 60, 0.16);
  color: #b9770e;
}
</style>
